// TODO: wrap IconButton, MenuItem, Popover, Select, TextField as foundation primitives
import {
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
} from "@mui/material";
import * as React from "react";

import {
  type TimeDisplayMode,
  useWorkspaceTimeBounds,
} from "@/shared/components/visualization/WorkspaceCtx";
import type { CanonicalDataType } from "@/shared/domain/topics";
import {
  AddIcon,
  CheckIcon,
  DeleteOutlineIcon,
} from "@/shared/foundation/icons/mui";
import { useDebouncedCallback } from "@/shared/hooks";
import { usePopupState } from "@/shared/hooks/usePopupState";
import { TimeBoundsSource } from "@/shared/state/visualization/schema";

import styles from "./ColumnFilterPopover.module.css";
import { filterStageEqual } from "./commit";
import {
  conditionMeta,
  getConditionsForDataType,
  getFilterPlaceholder,
  isDatetimeColumn,
  isValuelessCondition,
  needsSecondValue,
  shouldUseDatetimePicker,
} from "./conditionMeta";
import { DatetimeFilterInput } from "./DatetimeFilterInput";
import {
  type FilterStageColorVars,
  stageColorStyle,
} from "./filterStageColors";
import { isValidRegex } from "./regexUtils";
import { canCommitLogtime, resolveLogTimeValue } from "./resolveLogTimeValue";
import { seedLogTimeBuffer } from "./seedLogTimeBuffer";
import {
  type FilterCondition,
  type FilterStage,
  type StageCombinator,
} from "./state";

export interface ColumnFilterPopoverProps {
  popup: ReturnType<typeof usePopupState<void>>;
  columnName: string;
  /** Logical column key; keys into colorMap for highlight colors. */
  columnId?: string;
  /** Map from `${columnId}:${stageIndex}` to a stage-color slot. Owned by LogPanel. */
  colorMap: Map<string, number>;
  /** Color variables for each stage slot. Owned by LogPanel. */
  stageColors: FilterStageColorVars[];
  stages: FilterStage[];
  dataType?: CanonicalDataType;
  timeDisplayMode: TimeDisplayMode;
  combinator: StageCombinator;
  onStageChange: (stageIndex: number, stage: FilterStage) => void;
  onStageAdd: (stage: FilterStage) => void;
  onStageDelete: (stageIndex: number) => void;
  onCombinatorChange: (combinator: StageCombinator) => void;
}

/**
 * A popover used to apply one or more filter stages to a log panel column.
 *
 * Each stage has a condition dropdown and, depending on the condition and
 * data type, either a text input, two text inputs (for `between`), or a
 * datetime picker (for timestamp columns in Local/UTC mode). When a
 * timestamp column is in a relative time mode (Log / Elapsed / ElapsedTime),
 * the text input is paired with a confirm button that resolves the typed
 * display-format value to a canonical nanosecond epoch string.
 *
 * Multiple stages are combined with the shared combinator (AND / OR).
 *
 * The popover also contains an "Add condition" button to append new stages,
 * and a delete button on each stage when more than one stage exists.
 */
export function ColumnFilterPopover({
  popup,
  columnName,
  columnId,
  colorMap,
  stageColors,
  stages,
  dataType,
  timeDisplayMode,
  combinator,
  onStageChange,
  onStageAdd,
  onStageDelete,
  onCombinatorChange,
}: ColumnFilterPopoverProps) {
  const availableConditions = getConditionsForDataType(dataType);
  return (
    <Popover
      disableRestoreFocus
      disableAutoFocus
      open={popup.isOpen}
      anchorEl={popup.anchor}
      onClose={() => popup.close()}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          popup.close();
        }}
      >
        <div className={styles.treeRoot}>
          <span className={styles.columnHeader}>{columnName}:</span>
          <div className={styles.stageList}>
            {stages.map((stage, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div className={styles.combinatorRow}>
                    <div className={styles.traceLine} />
                    <button
                      className={styles.combinatorToggle}
                      onClick={() =>
                        onCombinatorChange(combinator === "or" ? "and" : "or")
                      }
                      type="button"
                      aria-label={`Toggle filter combinator, currently ${combinator === "or" ? "OR" : "AND"}`}
                    >
                      {combinator === "or" ? "OR" : "AND"}
                    </button>
                  </div>
                )}
                <div
                  className={styles.stageEntry}
                  style={
                    columnId !== undefined &&
                    colorMap.has(`${columnId}:${index}`)
                      ? stageColorStyle(
                          colorMap.get(`${columnId}:${index}`)!,
                          stageColors,
                        )
                      : undefined
                  }
                >
                  <div className={styles.traceLine} />
                  <div className={styles.stageCard}>
                    <StageRow
                      stage={stage}
                      stageNumber={index + 1}
                      availableConditions={availableConditions}
                      autoFocusTextField={index === stages.length - 1}
                      showDelete={stages.length > 1}
                      dataType={dataType}
                      timeDisplayMode={timeDisplayMode}
                      onChange={(updated) => onStageChange(index, updated)}
                      onDelete={() => onStageDelete(index)}
                    />
                  </div>
                </div>
              </React.Fragment>
            ))}
            <div className={styles.traceEnd}>
              <div className={styles.traceLineDashed} />
              <IconButton
                size="small"
                title="Add condition"
                aria-label="Add another filter condition"
                onClick={() =>
                  onStageAdd({ value: "", condition: availableConditions[0] })
                }
                className={styles.addButton}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
      </form>
    </Popover>
  );
}

interface StageRowProps {
  stage: FilterStage;
  stageNumber: number;
  availableConditions: FilterCondition[];
  autoFocusTextField: boolean;
  showDelete: boolean;
  dataType?: CanonicalDataType;
  timeDisplayMode: TimeDisplayMode;
  onChange: (stage: FilterStage) => void;
  onDelete: () => void;
}

/**
 * Delay before an in-flight plain-text edit is committed through `onChange`.
 * This is the only delay between typing and filtering: keystrokes never write
 * panel config directly (a config write re-renders the whole workspace).
 */
const PLAIN_TEXT_COMMIT_DEBOUNCE_MS = 300;

function StageRow({
  stage,
  stageNumber,
  availableConditions,
  autoFocusTextField,
  showDelete,
  dataType,
  timeDisplayMode,
  onChange,
  onDelete,
}: StageRowProps) {
  const condition = stage.condition;
  const isRegex = condition === "matches_regex";
  const isNumeric = conditionMeta[condition].numericOnly;
  const hideTextField = isValuelessCondition(condition);
  const showSecondValue = needsSecondValue(condition);
  const useDatetimePicker = shouldUseDatetimePicker(
    dataType,
    timeDisplayMode,
    condition,
  );

  const timeBounds = useWorkspaceTimeBounds({
    timeBoundsSource: TimeBoundsSource.FilesInWorkspace,
  });
  const earliestTime = timeBounds.earliest ?? null;

  // For non-datetime logtime fields, the stored value is a canonical nanosecond
  // epoch string. The user types in display-mode format (e.g. seconds, hh:mm:ss)
  // and confirms with a checkmark button, which resolves to nanoseconds.
  const isLogTimeText =
    !useDatetimePicker && isDatetimeColumn(dataType) && isNumeric;
  const inputType = isLogTimeText || !isNumeric ? "text" : "number";

  // The local buffers hold the in-flight text for both text-input kinds.
  // Logtime: a display-format view of the canonical nanosecond `stage.value`,
  // committed via the confirm button. Plain text: a draft committed on a
  // short debounce, on blur, and when the row unmounts (Escape closing the
  // popover), so keystrokes never write panel config directly.
  //
  // `syncedStageRef` names the stage the buffers belong to: the one they were
  // seeded from, or the one this row committed. The buffers are re-seeded only
  // when a stage matching neither arrives (layout hydration, an AI tool), or
  // when the display format changes. Focus leaving a box is not a reason to
  // re-seed: an unconfirmed logtime draft must survive the mouse press on its
  // own confirm button. Rows are keyed by array index, so replacing a column's
  // stages can hand this instance a stage it never saw; re-seeding then drops
  // the draft instead of pairing it with the incoming condition.
  const seedBuffer = React.useCallback(
    (value: string) =>
      isLogTimeText
        ? seedLogTimeBuffer(value, isLogTimeText, timeDisplayMode, earliestTime)
        : value,
    [isLogTimeText, timeDisplayMode, earliestTime],
  );
  const [localValue, setLocalValue] = React.useState(() =>
    seedBuffer(stage.value),
  );
  const [localValue2, setLocalValue2] = React.useState(() =>
    seedBuffer(stage.value2 ?? ""),
  );

  const hasRegexError =
    isRegex && localValue !== "" && !isValidRegex(localValue);

  // Latest drafts and props for the debounced commit, which must not close
  // over stale renders.
  const draftRef = React.useRef({
    value: localValue,
    value2: localValue2,
    condition,
    showSecondValue,
    onChange,
  });
  draftRef.current = {
    value: localValue,
    value2: localValue2,
    condition,
    showSecondValue,
    onChange,
  };

  const syncedStageRef = React.useRef(stage);
  const hasPendingDraftRef = React.useRef(false);

  const commitDraft = React.useCallback(() => {
    hasPendingDraftRef.current = false;
    const draft = draftRef.current;
    const committed: FilterStage = {
      value: draft.value,
      condition: draft.condition,
      ...(draft.showSecondValue && { value2: draft.value2 }),
    };
    // Claim the stage this row is committing, so the effect recognizes it when
    // it returns in props and leaves the buffers alone.
    syncedStageRef.current = committed;
    draft.onChange(committed);
  }, []);
  const debouncedCommitDraft = useDebouncedCallback(
    commitDraft,
    PLAIN_TEXT_COMMIT_DEBOUNCE_MS,
  );

  const seededWithRef = React.useRef(seedBuffer);
  React.useEffect(() => {
    if (
      seedBuffer === seededWithRef.current &&
      filterStageEqual(stage, syncedStageRef.current)
    ) {
      return;
    }
    seededWithRef.current = seedBuffer;
    syncedStageRef.current = stage;
    hasPendingDraftRef.current = false;
    debouncedCommitDraft.cancel();
    setLocalValue(seedBuffer(stage.value));
    setLocalValue2(seedBuffer(stage.value2 ?? ""));
  }, [stage, seedBuffer, debouncedCommitDraft]);

  // The debounce cancels its timer on unmount; a draft still waiting on it is
  // committed instead of lost.
  React.useEffect(
    () => () => {
      if (hasPendingDraftRef.current) {
        commitDraft();
      }
    },
    [commitDraft],
  );

  const onTextBlur = React.useCallback(() => {
    if (isLogTimeText) {
      // Logtime commits only via the confirm button.
      return;
    }
    debouncedCommitDraft.cancel();
    commitDraft();
  }, [isLogTimeText, debouncedCommitDraft, commitDraft]);

  return (
    <>
      <div className={styles.selectRow}>
        <Select
          size="small"
          fullWidth
          value={condition}
          aria-label={`Condition for stage ${stageNumber}`}
          onChange={(event) => {
            const nextCondition = event.target.value as FilterCondition;
            if (isValuelessCondition(nextCondition)) {
              onChange({ value: "", condition: nextCondition });
              return;
            }
            // Fold any in-flight plain-text draft into the condition change
            // so the two edits can't race; logtime keeps its canonical value.
            const value = isLogTimeText ? stage.value : localValue;
            const value2 = isLogTimeText ? stage.value2 : localValue2;
            onChange({
              value,
              condition: nextCondition,
              ...(needsSecondValue(nextCondition) &&
                value2 !== undefined && { value2 }),
            });
          }}
        >
          {availableConditions.map((key) => (
            <MenuItem key={key} value={key}>
              {conditionMeta[key].label}
            </MenuItem>
          ))}
        </Select>
        {showDelete && (
          <IconButton
            size="small"
            className={styles.inlineDeleteButton}
            onClick={onDelete}
            title="Remove condition"
            aria-label="Delete filter stage"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {useDatetimePicker &&
        (() => {
          return showSecondValue ? (
            <DatetimeFilterInput
              mode="between"
              layout="combined"
              from={stage.value}
              to={stage.value2 ?? ""}
              onChange={(from, to) =>
                onChange({ value: from, condition, value2: to })
              }
              timeDisplayMode={timeDisplayMode}
              earliestTime={earliestTime}
              textFieldProps={{ fullWidth: true, variant: "outlined" }}
              disablePopoverRestoreFocus
            />
          ) : (
            <DatetimeFilterInput
              mode="single"
              value={stage.value}
              onChange={(v) => onChange({ value: v, condition })}
              timeDisplayMode={timeDisplayMode}
              earliestTime={earliestTime}
              textFieldProps={{ fullWidth: true, variant: "outlined" }}
              disablePopoverRestoreFocus
            />
          );
        })()}
      {!useDatetimePicker && !hideTextField && (
        <TextField
          autoFocus={autoFocusTextField}
          fullWidth
          size="small"
          type={inputType}
          placeholder={getFilterPlaceholder(isRegex, showSecondValue)}
          variant="outlined"
          value={localValue}
          error={
            hasRegexError ||
            (isLogTimeText &&
              localValue.trim() !== "" &&
              resolveLogTimeValue(localValue, timeDisplayMode, earliestTime) ===
                null)
          }
          onBlur={onTextBlur}
          onChange={(event) => {
            setLocalValue(event.target.value);
            if (!isLogTimeText) {
              hasPendingDraftRef.current = true;
              debouncedCommitDraft();
            }
          }}
        />
      )}
      {!useDatetimePicker && showSecondValue && (
        <TextField
          fullWidth
          size="small"
          type={inputType}
          placeholder="Max"
          variant="outlined"
          value={localValue2}
          error={
            isLogTimeText &&
            localValue2.trim() !== "" &&
            resolveLogTimeValue(localValue2, timeDisplayMode, earliestTime) ===
              null
          }
          onBlur={onTextBlur}
          onChange={(event) => {
            setLocalValue2(event.target.value);
            if (!isLogTimeText) {
              hasPendingDraftRef.current = true;
              debouncedCommitDraft();
            }
          }}
        />
      )}
      {isLogTimeText &&
        (() => {
          const resolved = resolveLogTimeValue(
            localValue,
            timeDisplayMode,
            earliestTime,
          );
          const resolved2 = showSecondValue
            ? resolveLogTimeValue(localValue2, timeDisplayMode, earliestTime)
            : undefined;
          const hasError =
            (localValue.trim() !== "" && resolved === null) ||
            (showSecondValue &&
              localValue2.trim() !== "" &&
              resolved2 === null);
          const timeRangeUnavailable = !canCommitLogtime(
            timeDisplayMode,
            earliestTime,
          );
          return (
            <button
              type="button"
              className={styles.confirmButton}
              disabled={
                hasError ||
                localValue.trim() === "" ||
                (showSecondValue && localValue2.trim() === "") ||
                timeRangeUnavailable
              }
              onClick={() => {
                if (resolved === null || timeRangeUnavailable) {
                  return;
                }
                if (showSecondValue && resolved2 === null) {
                  return;
                }
                if (showSecondValue) {
                  onChange({
                    value: resolved,
                    condition,
                    value2: resolved2 ?? "",
                  });
                } else {
                  onChange({ value: resolved, condition });
                }
              }}
              title={
                timeRangeUnavailable
                  ? "Time range unavailable"
                  : "Confirm value"
              }
              aria-label="Confirm filter value"
            >
              <CheckIcon fontSize="small" />
            </button>
          );
        })()}
    </>
  );
}
